import { auth } from '@/core/firebase/client'

/**
 * The player's write path into the OTHER project. Slice 7.
 *
 * Three calls, all landing on clubmanager-hosted Cloud Functions that verify
 * this app's ID token manually (Option B). The token travels in the BODY, not
 * the `Authorization` header, because a clubmanager `onCall` would auto-verify
 * the header against ITS project and reject a usersmatch audience — which is
 * the whole reason the bridge was one-way until now.
 *
 * ── HOW THIS DIFFERS FROM `use-club-availability` ───────────────────────────
 * That one degrades silently: it is an overlay on a screen that works without
 * it, so every failure resolves to "we do not know" and the grid says so.
 *
 * These do NOT. A player pressed «Reservar» and is waiting for an answer about
 * a court. Silence is the one response that cannot be given — they would leave
 * not knowing whether they have a booking. So every failure here returns a
 * TYPED outcome the screen renders as a sentence.
 *
 * §3b still holds, in the form that applies to a write: this app never crashes,
 * never spins forever, and never claims a booking it did not get. When
 * clubmanager is unreachable the answer is "no hemos podido reservar" and the
 * club's phone number is still on screen — the same fallback the whole feature
 * degrades to.
 */

/** Longer than the availability read's 4s: this is the request the player is
 *  actually waiting on, and giving up early on a write risks reporting failure
 *  for a booking that in fact committed. */
const WRITE_TIMEOUT_MS = 15_000

const FUNCTIONS_BASE = () =>
  (import.meta.env['VITE_CLUBMANAGER_FUNCTIONS_BASE'] as string | undefined) ?? ''

/**
 * Every way a booking attempt can end, as one closed set.
 *
 * `code` is what the screen branches on, and the codes are deliberately not
 * collapsed: "that slot just went" invites another tap, "this club stopped
 * taking online bookings" does not, and "we could not reach the club" means try
 * again in a minute. Rendering the three the same sends someone tapping at a
 * door that is never opening.
 */
export type BookingFailure =
  | 'slot_taken'
  | 'module_off'
  | 'court_unavailable'
  | 'unauthenticated'
  | 'hold_gone'
  | 'not_yours'
  | 'unreachable'
  | 'internal'

export interface HoldResult {
  ok: true
  holdId: string
  expiresMs: number
  dateKey: string
}

export interface ConfirmResult {
  ok: true
  bookingId: string
}

export interface BookingError {
  ok: false
  code: BookingFailure
  message: string
}

/** Spanish, player-facing, one sentence per outcome. */
const MESSAGES: Record<BookingFailure, string> = {
  slot_taken: 'Esa hora acaba de ocuparse. Prueba con otra.',
  module_off: 'Este club ya no acepta reservas desde la app. Escríbele para reservar.',
  court_unavailable: 'Esa pista no está disponible ahora mismo.',
  unauthenticated: 'Inicia sesión para reservar.',
  hold_gone: 'Se agotó el tiempo para confirmar. Vuelve a elegir la hora.',
  not_yours: 'Esa reserva no es tuya.',
  unreachable: 'No hemos podido contactar con el club. Inténtalo de nuevo en un momento.',
  internal: 'No hemos podido completar la reserva. Inténtalo de nuevo.',
}

function fail(code: BookingFailure): BookingError {
  return { ok: false, code, message: MESSAGES[code] }
}

/**
 * The caller's Firebase ID token, or null when signed out.
 *
 * NOT cached. A stale token is rejected by the CF as `unauthenticated`, which
 * the player reads as "inicia sesión" while already signed in — a dead end with
 * no way out. The SDK caches it internally and refreshes when it is close to
 * expiry, so asking each time costs nothing and removes the failure mode.
 */
async function idToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  try {
    return await user.getIdToken()
  } catch {
    return null
  }
}

const FAILURE_CODES = new Set<string>([
  'slot_taken', 'module_off', 'court_unavailable', 'unauthenticated',
  'hold_gone', 'not_yours', 'internal',
])

async function post<T>(path: string, body: Record<string, unknown>): Promise<T | BookingError> {
  const base = FUNCTIONS_BASE()
  if (!base) return fail('unreachable')

  const token = await idToken()
  if (!token) return fail('unauthenticated')

  const timer = new AbortController()
  const timeout = setTimeout(() => timer.abort(), WRITE_TIMEOUT_MS)
  try {
    const res = await fetch(`${base}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, idToken: token }),
      signal: timer.signal,
    })
    const parsed: unknown = await res.json().catch(() => null)
    if (typeof parsed !== 'object' || parsed === null) return fail('unreachable')
    const p = parsed as Record<string, unknown>

    if (res.ok && p['ok'] === true) {
      // A malformed success is uncertain, never a booking receipt.
      const required = path === 'create_booking_hold' ? 'holdId' : path === 'confirm_booking' ? 'bookingId' : null
      if (required && (typeof p[required] !== 'string' || !p[required].trim())) return fail('unreachable')
      return p as T
    }
    // Trust the CF's code only when we recognise it. An unknown code from a
    // future deploy must not render as a blank message — it degrades to the
    // generic failure, which at least tells the player what to do next.
    const code = typeof p['code'] === 'string' && FAILURE_CODES.has(p['code'])
      ? (p['code'] as BookingFailure)
      : 'internal'
    return fail(code)
  } catch {
    // Network, CORS, DNS, abort. The club is not reachable; say exactly that
    // rather than implying the slot was refused.
    return fail('unreachable')
  } finally {
    clearTimeout(timeout)
  }
}

/** Claim a court+slot for a few minutes. */
export function createBookingHold(input: {
  clubId: string
  courtId: string
  startMs: number
  durationMinutes: number
  playerName?: string
  playerPhone?: string
}): Promise<HoldResult | BookingError> {
  return post<HoldResult>('create_booking_hold', input)
}

/** Promote a hold into a real booking in the club's calendar. */
export function confirmBooking(input: {
  clubId: string
  holdId: string
}): Promise<ConfirmResult | BookingError> {
  return post<ConfirmResult>('confirm_booking', input)
}

/** Cancel a booking this player made through the app. */
export function cancelOnlineBooking(input: {
  clubId: string
  bookingId: string
}): Promise<{ ok: true } | BookingError> {
  return post<{ ok: true }>('cancel_online_booking', input)
}

export function isBookingError(r: unknown): r is BookingError {
  return typeof r === 'object' && r !== null && (r as { ok?: unknown }).ok === false
}
